import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import {
  getImpostazioniPrezzi,
  updatePercentualeIbs,
} from '../services/api';

const RicarichiIBSPage = () => {
  // --- Ricarico IBS ---
  const [percentualeIbs, setPercentualeIbs] = useState(0);
  const [markupFissoIbs, setMarkupFissoIbs] = useState(0);
  const [ricalcolaEsistenti, setRicalcolaEsistenti] = useState(false);
  const [salvandoPercentuale, setSalvandoPercentuale] = useState(false);
  // Spedizione a carico del cliente su IBS e margine minimo (costi reali dalla sezione Corrieri)
  const [spedizioneCliente, setSpedizioneCliente] = useState(5.9);
  // Classi logistiche Mirakl: Piccolo sotto la soglia, Medio da soglia in su (tariffe impostate su Mirakl)
  const [spedizionePiccolo, setSpedizionePiccolo] = useState('');
  const [sogliaClasse, setSogliaClasse] = useState(8);
  const [margineMinimo, setMargineMinimo] = useState(1);
  const [infoCosti, setInfoCosti] = useState(null);

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await getImpostazioniPrezzi();
        setPercentualeIbs(res.data.ibs_markup_percentuale);
        setMarkupFissoIbs(res.data.ibs_markup_fisso);
        setSpedizioneCliente(res.data.ibs_spedizione_cliente);
        setSpedizionePiccolo(res.data.ibs_spedizione_piccolo ?? '');
        setSogliaClasse(res.data.ibs_soglia_classe_piccolo ?? 8);
        setMargineMinimo(res.data.ibs_margine_minimo);
        setInfoCosti(res.data);
      } catch (err) {
        toast.error('Errore nel caricamento impostazioni: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSalvaPercentuale = async () => {
    if (ricalcolaEsistenti && !window.confirm(
      'Tutti i prezzi IBS verranno riportati al listino (prezzo base + ricarico) e ripubblicati su Mirakl.\n' +
      'Gli eventuali repricing già applicati vanno persi: a fine periodo andrà rilanciato il repricing.\n\nProcedere?')) {
      return;
    }
    setSalvandoPercentuale(true);
    try {
      const res = await updatePercentualeIbs(parseFloat(percentualeIbs) || 0, parseFloat(markupFissoIbs) || 0, ricalcolaEsistenti, {
        ibs_spedizione_cliente: parseFloat(spedizioneCliente) || 0,
        ibs_spedizione_piccolo: parseFloat(spedizionePiccolo) || 0,
        ibs_soglia_classe_piccolo: parseFloat(sogliaClasse) || 8,
        ibs_margine_minimo: parseFloat(margineMinimo) || 0,
      });
      setInfoCosti(res.data);
      setRicalcolaEsistenti(false);
      toast.success('Impostazioni IBS salvate' + (ricalcolaEsistenti ? ' e prezzi IBS riportati al listino' : ''));
    } catch (err) {
      toast.error('Errore nel salvataggio: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSalvandoPercentuale(false);
    }
  };

  if (loading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }

  return (
    <Container fluid className="py-4">
      <h2 className="fw-bold mb-4">
        <FontAwesomeIcon icon={faTags} className="me-2 text-info" />
        Ricarichi IBS
      </h2>

      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold">Prezzo IBS — ricarico sul prezzo base e spedizione</Card.Header>
        <Card.Body>
          <p className="text-muted">
            Il prezzo IBS di listino è <strong>Prezzo Base × (1 + percentuale) + markup fisso</strong> (arrotondato a .90) e non dipende più
            dal prezzo negozio. Su IBS la spedizione la paga il cliente (cifra impostata in Mirakl, qui sotto per i calcoli).
            Il ricarico serve per i periodi particolari (es. Natale); il repricing porta poi i prezzi al mercato.
          </p>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Label className="fw-semibold">Percentuale</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number" min={0} step={0.5}
                  value={percentualeIbs}
                  onChange={e => setPercentualeIbs(e.target.value)}
                />
                <span className="input-group-text">%</span>
              </div>
            </Col>
            <Col md={2}>
              <Form.Label className="fw-semibold">Markup fisso</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number" min={0} step={0.1}
                  value={markupFissoIbs}
                  onChange={e => setMarkupFissoIbs(e.target.value)}
                />
                <span className="input-group-text">€</span>
              </div>
            </Col>
            <Col md={4}>
              <Form.Check
                type="checkbox"
                label="Riporta subito tutti i prezzi IBS al listino (cancella i repricing applicati)"
                checked={ricalcolaEsistenti}
                onChange={e => setRicalcolaEsistenti(e.target.checked)}
              />
            </Col>
            <Col md={2}>
              <Button variant="info" onClick={handleSalvaPercentuale} disabled={salvandoPercentuale}>
                {salvandoPercentuale ? <Spinner as="span" animation="border" size="sm" /> : 'Salva'}
              </Button>
            </Col>
          </Row>
          <Row className="g-3 align-items-end mt-1">
            <Col md={2}>
              <Form.Label className="fw-semibold">Spedizione Medio (da {parseFloat(sogliaClasse) || 8} €)</Form.Label>
              <div className="input-group">
                <Form.Control type="number" min={0} step={0.1} value={spedizioneCliente} onChange={e => setSpedizioneCliente(e.target.value)} />
                <span className="input-group-text">€</span>
              </div>
              <Form.Text className="text-muted">Come su Mirakl (ivata).</Form.Text>
            </Col>
            <Col md={2}>
              <Form.Label className="fw-semibold">Spedizione Piccolo</Form.Label>
              <div className="input-group">
                <Form.Control type="number" min={0} step={0.1} value={spedizionePiccolo} placeholder="come Medio" onChange={e => setSpedizionePiccolo(e.target.value)} />
                <span className="input-group-text">€</span>
              </div>
              <Form.Text className="text-muted">Vuoto = uguale al Medio.</Form.Text>
            </Col>
            <Col md={2}>
              <Form.Label className="fw-semibold">Soglia Piccolo/Medio</Form.Label>
              <div className="input-group">
                <Form.Control type="number" min={0.01} step={0.5} value={sogliaClasse} onChange={e => setSogliaClasse(e.target.value)} />
                <span className="input-group-text">€</span>
              </div>
              <Form.Text className="text-muted">Prezzo articolo sotto cui è Piccolo.</Form.Text>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold">Margine minimo per vendita (repricing)</Form.Label>
              <div className="input-group">
                <Form.Control type="number" min={0} step={0.1} value={margineMinimo} onChange={e => setMargineMinimo(e.target.value)} />
                <span className="input-group-text">€</span>
              </div>
              <Form.Text className="text-muted">Netto, dopo commissione, spedizione e costo merce.</Form.Text>
            </Col>
            <Col md={6}>
              {infoCosti && (
                <div className="small text-muted border rounded p-2">
                  Costi reali usati nei calcoli (si modificano nella sezione Corrieri e Canoni Marketplace):<br />
                  spedizione a nostro carico: <strong>{(infoCosti.costo_spedizione_netto ?? 0).toFixed(2)} €</strong> netti
                  {infoCosti.ibs_corriere_riferimento_nome ? ` (${infoCosti.ibs_corriere_riferimento_nome})` : ''}
                  {' · '}commissione IBS netta: <strong>{(infoCosti.commissione_netta_pct ?? 0).toFixed(2)}%</strong> di articolo + spedizione
                  {infoCosti.ultimo_repricing ? ` · ultimo repricing: ${new Date(infoCosti.ultimo_repricing + 'Z').toLocaleString('it-IT')}` : ''}
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RicarichiIBSPage;
